import { faGauge, faGaugeMax, faGaugeMin } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { paraphrase } from '@application/ai-prompts/paraphraser/command'

import AiGenerator from '@ui/shared/components/AiGenerator'
import { convertHtmlToText } from '@ui/shared/utils/html-to-text-converter'

import './Paraphraser.css'

const faqs = [
  {
    question: 'How do I use the Paraphraser?',
    answer:
      "Enter your sentence or paragraph into the text box and click one of the three paraphrasing levels. You'll instantly get your text rewritten - you'll see small edits, word replacements, and even rewrites of phrases.",
  },
  {
    question: 'What are the three levels that the Paraphraser shows?',
    answer:
      "The three levels paraphrase your sentence to a different degree, so choose them depending on how heavily you want your text rewritten. If you're looking to see what changes you can make to a sentence, select 'High', which is most creative.",
  },
  {
    question:
      'If I use the Paraphraser to change an existing text, do I still need to cite the source?',
    answer:
      "Yes; you should always cite your source, no matter how much you change the language. The only thing you don't need to do after paraphrasing is adding quotation marks, which is only required when you cite a source word by word. In all other cases, always add a citation if the ideas or findings are not your own.",
  },
  {
    question: 'Is the Paraphraser free to use?',
    answer:
      "Yes, you can use it for free, with a daily quota that we set to avoid abuse. It's also available in Writefull for Word and Writefull for Overleaf.",
  },
  {
    question: 'Do you have an API of the Paraphraser?',
    answer:
      'Yes, you can also use our API to automatically generate paraphrases. Contact us at enterprise@writefull.com for more information.',
  },
  {
    question: 'What happens to the texts that I enter into the Paraphraser? ',
    answer:
      "Nothing at all - we don't store anything. Your text is encrypted and sent to Writefull's servers where it is processed and removed straight after.",
  },
  {
    question: "I'm getting a message that says 'Quota exceeded'. What's this?",
    answer:
      "We set a daily quota to avoid our servers getting too many requests. If you've run many paraphrases, you'll get blocked temporarily, and get the 'Quota exceeded' message. Your quota resets every 12 hours, so you'll be able to use the Paraphraser again after that.",
  },
]

const tableFeatures = [
  { feature: `🙌 ${'\u00A0'} Free for use`, description: 'Free paraphraser, no sign up needed.' },
  {
    feature: `🔒 ${'\u00A0'} Safe & secure`,
    description: 'Your data is encrypted and never stored.',
  },
  {
    feature: `🚀 ${'\u00A0'} Rephrase sentences`,
    description: 'Minor, medium and high levels of paraphrasing done fast!',
  },
  {
    feature: `👨‍💻 ${'\u00A0'} API access`,
    description: 'API access to the Paraphraser on request.',
  },
]

const examples = [
  'Poor drainage of household wastewater and rainwater causes a number of environmental and health hazards, for example, formation of stagnant pools of water that provides breeding sites for mosquitoes. Uncollected refuse provide breeding grounds for disease vectors, such as cockroaches, flies, and rats. The most common approach used to control these pests in enclosed environments like in homes is use of pesticides, particularly insecticides and rodenticides. Poor use and storage of pesticides especially in poor urban settings has contributed to the health burden in these settings [12].',
]

export default function Paraphraser() {
  return (
    <AiGenerator
      label='Enter a sentence or paragraph'
      actions={[
        {
          name: 'Low',
          code: 'low',
          tooltip: 'Minor changes to the original text',
          icon: <FontAwesomeIcon icon={faGaugeMin} />,
        },
        {
          name: 'Medium',
          code: 'medium',
          tooltip: 'Changes at word and phrase level',
          icon: <FontAwesomeIcon icon={faGauge} />,
        },
        {
          name: 'High',
          code: 'high',
          tooltip: 'Most changes at phrase and syntactic level',
          icon: <FontAwesomeIcon icon={faGaugeMax} />,
        },
      ]}
      skeleton={{ type: 'text', bars: 4 }}
      faqs={{ faqs, tableFeatures, widgetName: 'Paraphraser' }}
      examples={examples}
      minLength={50}
      maxLength={2000}
      copyToClipboardOutputConverter={convertHtmlToText}
      onGenerate={paraphrase}
    />
  )
}
